import React from 'react';
import { 
  Calendar, 
  Utensils, 
  Users, 
  LogOut, 
  Menu, 
  ChevronLeft, 
  Building2, 
  Settings, 
  CreditCard, 
  DollarSign, 
  BarChart3, 
  ChevronDown, 
  ChevronRight,
  Package,
  Wine,
  Brain,
  FileText,
  Sofa,
  BuildingIcon,
  Armchair,
  Warehouse,
  Star
} from 'lucide-react';
import { useNavigationStore } from '../stores/navigationStore';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../utils/cn';

const LeftNavigation: React.FC = () => {
  const { currentScreen, navigateTo, isSidebarCollapsed, toggleSidebar, categories, toggleCategory } = useNavigationStore();
  const { logout } = useAuthStore();

  const handleLogout = () => {
    logout();
  };

  // Icon mapping for screens
  const getScreenIcon = (screenId: string) => {
    const iconMap: Record<string, any> = {
      'reservation-calendar': Calendar,
      'banquet-management': Armchair,
      'corporate-outings': Building2,
      'room-management': Warehouse,
      'inventory': Package,
      'restaurant-management': Utensils,
      'bar-management': Wine,
      'finance-accounting': DollarSign,
      'insights': Brain,
      'reporting-center': FileText,
      'crm': Users,
      'loyalty': Star,
      'user-management': Users,
    };
    return iconMap[screenId] || Building2;
  };

  // Icon mapping for categories
  const getCategoryIcon = (iconName: string) => {
    const iconMap: Record<string, any> = {
      'Building2': Building2,
      'Settings': Settings,
      'CreditCard': CreditCard,
      'DollarSign': DollarSign,
      'BarChart3': BarChart3,
      'Users': Users,
    };
    return iconMap[iconName] || Building2;
  };

  // Screen title mapping
  const getScreenTitle = (screenId: string) => {
    const titleMap: Record<string, string> = {
      'reservation-calendar': 'Reservation Calendar',
      'banquet-management': 'Banquet Management',
      'corporate-outings': 'Corporate Outings',
      'room-management': 'Room Management',
      'inventory': 'Inventory',
      'restaurant-management': 'Restaurant Management',
      'bar-management': 'Bar Management',
      'finance-accounting': 'Finance & Accounting',
      'insights': 'AI Command Center',
      'reporting-center': 'Reporting Center',
      'crm': 'CRM',
      'loyalty': 'Loyalty Program',
      'user-management': 'Staff Management',
    };
    return titleMap[screenId] || screenId;
  };

  const getScreenSubtitle = (screenId: string) => {
    const subtitleMap: Record<string, string> = {
      'reservation-calendar': 'Room Management',
      'banquet-management': 'Hall Bookings & Events',
      'corporate-outings': 'Company Events',
      'room-management': 'Room Configuration',
      'inventory': 'Stock Management',
      'restaurant-management': 'Food & Orders',
      'bar-management': 'Beverage Service',
      'finance-accounting': 'Financial Reports',
      'insights': 'AI Insights & Actions',
      'reporting-center': 'Reports & Analytics',
      'crm': 'Customer Management',
      'loyalty': 'Rewards & Points',
      'user-management': 'Roles & Departments',
    };
    return subtitleMap[screenId] || '';
  };

  return (
    <div className={cn(
      "glass-card border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out",
      isSidebarCollapsed ? "w-16" : "w-64"
    )}>
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between min-h-[52px]">
        {!isSidebarCollapsed && (
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
              <img 
                src="/images/snowtable-logo-new.png" 
                alt="Snowtable Logo" 
                className="w-8 h-8 object-contain"
              />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-bold text-primary-500 truncate">Hotel Continental</h2>
              <p className="text-xs text-gray-500 truncate">powered by Snowtable</p>
            </div>
          </div>
        )}
        {isSidebarCollapsed && (
          <div className="w-8 h-8 flex items-center justify-center mx-auto">
            <img 
              src="/images/snowtable-logo-new.png" 
              alt="Snowtable Logo" 
              className="w-8 h-8 object-contain"
            />
          </div>
        )}
        <button
          onClick={toggleSidebar}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
        >
          {isSidebarCollapsed ? (
            <Menu className="w-5 h-5 text-gray-600" />
          ) : (
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* Navigation Categories */}
      <div className="flex-1 p-2 space-y-1 overflow-y-auto">
        {categories.map((category) => {
          const CategoryIcon = getCategoryIcon(category.icon);
          const isCategoryActive = category.screens.includes(currentScreen);
          
          return (
            <div key={category.id} className="space-y-1">
              {/* Category Header */}
              <button
                onClick={() => toggleCategory(category.id)}
                className={cn(
                  "w-full text-left rounded-lg transition-colors flex items-center gap-3 p-3",
                  isCategoryActive 
                    ? "bg-primary-50 text-primary-700 border border-primary-200" 
                    : "text-gray-700 hover:bg-gray-100"
                )}
                title={isSidebarCollapsed ? category.title : undefined}
              >
                <CategoryIcon className="w-5 h-5 flex-shrink-0" />
                {!isSidebarCollapsed && (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{category.title}</div>
                    </div>
                    <div className="flex-shrink-0">
                      {category.isCollapsed ? (
                        <ChevronRight className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </>
                )}
              </button>

              {/* Category Screens */}
              {!category.isCollapsed && !isSidebarCollapsed && (
                <div className="ml-4 space-y-1">
                  {category.screens.map((screenId) => {
                    const ScreenIcon = getScreenIcon(screenId);
                    const isActive = currentScreen === screenId;
                    const screenTitle = getScreenTitle(screenId);
                    const screenSubtitle = getScreenSubtitle(screenId);
                    
                    return (
                      <button
                        key={screenId}
                        onClick={() => navigateTo(screenId)}
                        className={cn(
                          "w-full text-left rounded-lg transition-colors flex items-center gap-3 p-3",
                          isActive 
                            ? "bg-primary-500 text-white" 
                            : "text-gray-600 hover:bg-gray-50"
                        )}
                      >
                        <ScreenIcon className="w-4 h-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate text-sm">{screenTitle}</div>
                          {screenSubtitle && (
                            <div className="text-xs opacity-75 truncate">{screenSubtitle}</div>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer - Fixed at bottom */}
      <div className="p-2 border-t border-gray-200 mt-auto">
        <button
          onClick={handleLogout}
          className={cn(
            "w-full text-left rounded-lg transition-colors flex items-center gap-3 p-3 text-gray-700 hover:text-red-600 hover:bg-red-50",
            isSidebarCollapsed && "justify-center"
          )}
          title={isSidebarCollapsed ? "Logout" : undefined}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          {!isSidebarCollapsed && <span>Logout</span>}
        </button>
      </div>
    </div>
  );
};

export default LeftNavigation;

